/**
 * アプリケーションのルートコンポーネント。
 *
 * - 認証状態に応じて Login / メインページを切り替える。
 * - 未認証時は `/login` と `/reset-password` のみ表示し、それ以外 (`/signup` を含む) は `/login` に集約される。
 *   アカウントは「ユーザー管理」からの招待でのみ作成できる (自己登録の画面は無い)。
 * - 認証済みの場合は `/login` を踏んでもメインページにリダイレクトする。
 * - ヘッダーのタイトルはトップページ (線区選択画面) へ戻るリンクとして機能する。
 * - 権限管理 (SBI-6): ログイン後に `GET /me/permissions` を取得し、ヘッダーナビと Route を
 *   許可された機能だけに絞る。許可されていない URL は許可された先頭の画面へ戻し、
 *   許可された画面が 1 つも無ければ「権限がありません」を表示する。
 *   ⚠️ 画面の出し分けは利便性のため。最終的な拒否は常にバックエンド (403) が行う。
 */
import { Fragment } from 'react';

import { Spinner } from './components/Spinner';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';

import { useAuth } from './auth/AuthProvider';
import { LoginPage } from './auth/LoginPage';
import { NewPasswordPage } from './auth/NewPasswordPage';
import { ResetPasswordPage } from './auth/ResetPasswordPage';
import type { AppConfig } from './config';
import { APP_TITLE } from './constants/appInfo';
import {
  HomeNavigationGuardProvider,
  useHomeNavigationGuard,
} from './contexts/HomeNavigationGuard';
import { ChimeMasterScreen } from './components/ChimeMasterScreen';
import { FreeTextTemplateScreen } from './components/FreeTextTemplateScreen';
import { LanguageHealthScreen } from './components/LanguageHealthScreen';
import { NoPermissionScreen, PermissionErrorScreen } from './components/NoPermissionScreen';
import { PermissionAssignScreen } from './components/PermissionAssignScreen';
import { PermissionGroupAdminScreen } from './components/PermissionGroupAdminScreen';
import { PhraseTemplateMasterScreen } from './components/PhraseTemplateMasterScreen';
import { RequireAnyFeature } from './components/RequireAnyFeature';
import { RequireFeature } from './components/RequireFeature';
import { SsmlDictionaryScreen } from './components/SsmlDictionaryScreen';
import { UserAdminScreen } from './components/UserAdminScreen';
import { PAGE_PATH_MASTER_HISTORY } from './constants/pagePaths';
import { PermissionProvider, usePermissions } from './contexts/PermissionContext';
import { clearStoredSelection } from './hooks/useRouteSelection';
import { AlertRecipientPage } from './pages/AlertRecipientPage';
import { BroadcastPointsPage } from './pages/BroadcastPointsPage';
import { DeviceRegistrationListPage } from './pages/DeviceRegistrationListPage';
import { MasterHistoryPage } from './pages/MasterHistoryPage';
import { MASTER_HISTORY_NAV_STATE } from './pages/masterHistoryView';
import { SsmlReadmePage } from './pages/SsmlReadmePage';
import { SsmlTuningPage } from './pages/SsmlTuningPage';
import { MASTER_HISTORY_FEATURE_KEYS } from './permissions/masterHistoryFeatures';
import { landingPath, visibleScreens } from './permissions/screens';

interface AppProps {
  config: AppConfig;
}

export default function App({ config }: AppProps): React.JSX.Element {
  const { isAuthenticated, isLoading, isNewPasswordRequired } = useAuth();

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-muted)' }}>
        <Spinner />
        セッションを確認中...
      </div>
    );
  }

  if (!isAuthenticated && isNewPasswordRequired) {
    // 招待ユーザが初期パスワードでログインした直後: 新パスワードを設定するまでメイン画面は出さない
    return <NewPasswordPage />;
  }

  if (!isAuthenticated) {
    // 未認証: ログイン / パスワードリセットのみ表示する
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // `.app-shell` は「ヘッダー固定 + メインが残り全部」の縦フレックス。
  // これによりメイン領域 (地図 / 一覧) がビューポート高さいっぱいまで広がる。
  return (
    // HomeNavigationGuardProvider: 編集モーダル表示中はヘッダーからのトップ遷移を
    // 無効化するため、ヘッダーとページで状態を共有する。
    // PermissionProvider: ログイン直後に GET /me/permissions を取得し、ナビと Route を絞る。
    // ログアウトでアンマウントされるため、別ユーザーに前の権限が残らない。
    <HomeNavigationGuardProvider>
      <PermissionProvider config={config}>
        <div className="app-shell">
          <AppHeader />
          <main className="app-main">
            <AuthenticatedRoutes config={config} />
          </main>
        </div>
      </PermissionProvider>
    </HomeNavigationGuardProvider>
  );
}

/**
 * 旧「共有テキスト」画面 (`/synced-texts`) から統合先の「任意テキスト」画面へリダイレクトする。
 * ブックマークや、旧形式の変更通知メールのリンク (`/synced-texts?route=...`) を壊さないため。
 * 線区絞り込みなどのクエリ文字列はそのまま引き継ぐ (統合先も同じ `?route=` を使う)。
 * 統合先への権限が無いユーザーは、統合先側の権限判定 (`RequireFeature`) で許可された先頭の画面へ戻される。
 */
function SyncedTextsRedirect(): React.JSX.Element {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/free-text-templates', search }} replace />;
}

/**
 * 認証済みユーザー向けの Routes。権限の取得状況に応じて表示を切り替える。
 *
 * - 取得中: ローディング。
 * - 取得失敗: 何も許可せず、再試行を案内する (安全側)。
 * - 許可された画面が 0 件: 「権限がありません。管理者に連絡してください」。
 * - それ以外: 許可された画面のみ Route を提供する。許可されていない URL は
 *   許可された先頭の画面へ戻す。
 *
 * ⚠️ 画面の出し分けは利便性のため。最終的な拒否は常にバックエンド (403) が行う。
 */
function AuthenticatedRoutes({ config }: AppProps): React.JSX.Element {
  const { status, features, errorMessage, reload } = usePermissions();

  if (status === 'loading') {
    return (
      <div
        style={{ padding: 24, textAlign: 'center', color: 'var(--color-muted)' }}
        data-testid="permission-loading"
      >
        <Spinner />
        権限を確認中...
      </div>
    );
  }
  if (status === 'error') {
    return <PermissionErrorScreen message={errorMessage} onRetry={reload} />;
  }

  const landing = landingPath(features);
  if (landing === null) {
    return <NoPermissionScreen />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <RequireFeature featureKey="broadcast_points">
            <BroadcastPointsPage config={config} />
          </RequireFeature>
        }
      />
      <Route
        path="/devices"
        element={
          <RequireFeature featureKey="devices">
            <DeviceRegistrationListPage config={config} />
          </RequireFeature>
        }
      />
      <Route
        path="/alert-recipients"
        element={
          <RequireFeature featureKey="alert_recipients">
            <AlertRecipientPage config={config} />
          </RequireFeature>
        }
      />
      {/* 要件 3・4: 放送開始 / 終了チャイム音マスタ (ChimeMaster) の管理画面 */}
      <Route
        path="/chimes"
        element={
          <RequireFeature featureKey="chimes">
            <ChimeMasterScreen config={config} />
          </RequireFeature>
        }
      />
      {/* Issue #449 (SBI-07): 多言語の放送定型文マスタ (PhraseTemplateMaster) の管理画面 */}
      <Route
        path="/phrase-templates"
        element={
          <RequireFeature featureKey="phrase_templates">
            <PhraseTemplateMasterScreen config={config} />
          </RequireFeature>
        }
      />
      {/* SBI-02: SSML 辞書マスタ (SsmlDictionaryMaster: 読み仮名 / アクセント辞典) の管理画面 */}
      <Route
        path="/ssml-dictionary"
        element={
          <RequireFeature featureKey="ssml_dictionary">
            <SsmlDictionaryScreen config={config} />
          </RequireFeature>
        }
      />
      {/* 任意テキスト (FreeTextTemplateMaster) + 共有テキスト (SyncedTextMaster) を 1 画面に統合した管理画面 */}
      <Route
        path="/free-text-templates"
        element={
          <RequireFeature featureKey="free_text_templates">
            <FreeTextTemplateScreen config={config} />
          </RequireFeature>
        }
      />
      {/* 旧「共有テキスト」画面の URL (ブックマーク / 一覧へのメールリンク) は統合先へ。`?route=` などのクエリも引き継ぐ */}
      <Route path="/synced-texts" element={<SyncedTextsRedirect />} />
      <Route
        path="/language-health"
        element={
          <RequireFeature featureKey="broadcast_points">
            <LanguageHealthScreen config={config} />
          </RequireFeature>
        }
      />
      {/*
        SSML チューニング (docs/ssml-tuning-plan.md)。
        放送文を SSML で調整し、Polly (話者 Takumi) で音声サンプルを作る画面。
      */}
      <Route
        path="/ssml-tuning"
        element={
          <RequireFeature featureKey="ssml_tuning">
            <SsmlTuningPage config={config} />
          </RequireFeature>
        }
      />
      {/*
        SSML Readme。チューニング画面から target="_blank" で開く構文リファレンス。
        単独 URL で自立表示できる (チューニング画面の状態に依存しない) 必要があるため、
        ナビタブは持たず Route のみを用意する。権限は音声チューニング (ssml_tuning) に従属する。
      */}
      <Route
        path="/ssml-readme"
        element={
          <RequireFeature featureKey="ssml_tuning">
            <SsmlReadmePage />
          </RequireFeature>
        }
      />
      {/*
        権限管理の管理画面 (権限管理 SBI-8)。それぞれ管理系の機能キー
        (user_admin / permission_group_admin / permission_assign) を持つユーザーにだけ表示する。
      */}
      <Route
        path="/admin/users"
        element={
          <RequireFeature featureKey="user_admin">
            <UserAdminScreen config={config} />
          </RequireFeature>
        }
      />
      <Route
        path="/admin/permission-groups"
        element={
          <RequireFeature featureKey="permission_group_admin">
            <PermissionGroupAdminScreen config={config} />
          </RequireFeature>
        }
      />
      <Route
        path="/admin/permission-assignments"
        element={
          <RequireFeature featureKey="permission_assign">
            <PermissionAssignScreen config={config} />
          </RequireFeature>
        }
      />
      {/*
        データ変更履歴 (マスタ変更履歴 SBI 7)。ヘッダーナビのタブ (機能キー master_history)、
        変更通知メールのリンク、各マスタ画面の導線から入る。閲覧権限は各マスタ画面と同じ機能に従い、
        master_history はルートの許可には使わない (タブの表示だけを制御する)。履歴を持つマスタの
        機能のいずれかを持てばルートを通す (RequireFeature は 1 つのキーしか受け取れないため
        RequireAnyFeature)。マスタ種別ごとの閲覧可否は画面内で判定する。
      */}
      <Route
        path={PAGE_PATH_MASTER_HISTORY}
        element={
          <RequireAnyFeature featureKeys={MASTER_HISTORY_FEATURE_KEYS}>
            <MasterHistoryPage config={config} />
          </RequireAnyFeature>
        }
      />
      {/* 認証済みでログインへ来た場合は、許可された先頭の画面へリダイレクト */}
      <Route path="/login" element={<Navigate to={landing} replace />} />
      {/* 未知の URL も、許可された先頭の画面へ (許可されていない画面の直 URL もここと同じ扱い) */}
      <Route path="*" element={<Navigate to={landing} replace />} />
    </Routes>
  );
}

function AppHeader(): React.JSX.Element {
  const { username, signOut } = useAuth();
  const navigate = useNavigate();
  const { blocked } = useHomeNavigationGuard();
  const { features } = usePermissions();
  // 許可された機能の画面だけをナビに出す (取得中 / 失敗時は空 = 何も出さない)。
  // ⚠️ 非表示は利便性のため。最終的な拒否は常にバックエンド (403)。
  const screens = visibleScreens(features);
  const languageHealthInsertionPoint =
    screens.find(
      (screen) => screen.featureKey === 'ssml_tuning' || screen.featureKey === 'user_admin',
    )?.featureKey ?? screens.at(-1)?.featureKey;

  /**
   * ヘッダータイトルクリック: トップページ (線区選択画面) に戻る。
   *
   * - 線区/方面の選択状態は URL クエリと sessionStorage に保持されているため、
   *   両方をクリアしてから `/` へ遷移する。
   * - 編集モーダル表示中 (`blocked`) は誤操作で入力内容を失わないよう無効化する。
   * - `/` が許可されていないユーザーは、`/` から許可された先頭の画面へリダイレクトされる。
   */
  const handleHomeClick = (): void => {
    if (blocked) return;
    clearStoredSelection();
    navigate('/');
  };

  return (
    <header className="app-header">
      <h1>
        <button
          type="button"
          className="app-header__home"
          data-testid="app-header-home"
          onClick={handleHomeClick}
          disabled={blocked}
          title={
            blocked
              ? '編集中はトップページに戻れません (先に保存またはキャンセルしてください)'
              : 'トップページ (線区選択) に戻る'
          }
        >
          {APP_TITLE}
        </button>
      </h1>

      {/*
        ヘッダー右側: ページ切替ナビ + ユーザー情報 をまとめて右寄せする。
        3 ブロックを space-between で並べるとナビだけが画面中央に浮いてしまい、
        どこに属する要素か分かりにくいため、右側でグループ化する。
      */}
      <div className="app-header__right">
        {screens.length > 0 && (
          <nav className="app-nav" aria-label="ページ切替">
            {/*
              並び順は CONTROL_PANEL_SCREENS (放送地点マスタ → ... → 音声チューニング → データ変更履歴 → 管理系 3 画面が最右)。
              SSML Readme (/ssml-readme) は別タブ運用のためナビには出さない。
            */}
            {screens.map((screen) => (
              <Fragment key={screen.featureKey}>
                {screen.featureKey === languageHealthInsertionPoint &&
                  features.has('broadcast_points') && (
                    <NavLink
                      to="/language-health"
                      onClick={(e) => {
                        if (blocked) e.preventDefault();
                      }}
                      aria-disabled={blocked}
                      tabIndex={blocked ? -1 : undefined}
                      className={({ isActive }) =>
                        `app-nav__link ${isActive ? 'app-nav__link--active' : ''}`
                      }
                      data-testid="nav-language-health"
                    >
                      言語別の生成状況
                    </NavLink>
                  )}
                <NavLink
                  to={screen.path}
                  // 履歴画面はタブから入ったことを state で伝える (遷移元が無いので「閉じる」等を出さないため)
                  state={
                    screen.path === PAGE_PATH_MASTER_HISTORY ? MASTER_HISTORY_NAV_STATE : undefined
                  }
                  end={screen.path === '/'}
                  onClick={(e) => {
                    if (blocked) e.preventDefault();
                  }}
                  aria-disabled={blocked}
                  tabIndex={blocked ? -1 : undefined}
                  className={({ isActive }) =>
                    `app-nav__link ${isActive ? 'app-nav__link--active' : ''}`
                  }
                  data-testid={screen.navTestId}
                >
                  {screen.labelLines ? (
                    <span className="app-nav__label-stack">
                      {screen.labelLines.map((line) => (
                        <span key={line}>{line}</span>
                      ))}
                    </span>
                  ) : (
                    screen.label
                  )}
                </NavLink>
              </Fragment>
            ))}
          </nav>
        )}

        <div className="user-info">
          <span>{username ?? ''}</span>
          <button
            type="button"
            onClick={() => {
              signOut();
              clearStoredSelection();
              navigate('/');
            }}
          >
            ログアウト
          </button>
        </div>
      </div>
    </header>
  );
}
